import { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BarChart3, QrCode, ArrowRight, CheckCircle, Check, Shield, BookOpen, Menu, X } from "lucide-react";
import logoOuraTrust from "@/assets/logo-ouratrust-v2.png";
import { useLanguage } from "@/hooks/useLanguage";
import { Footer } from "@/components/Footer";
import { SocialProofToast } from "@/components/SocialProofToast";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { TrustBar } from "@/components/TrustBar";
import { HeroIllustration } from "@/components/HeroIllustration";
import { QRKitPreview } from "@/components/QRKitPreview";
import { StepsSection } from "@/components/landing/StepsSection";
import { CentralizationHub } from "@/components/landing/CentralizationHub";
import { UnifiedPlatformSection } from "@/components/landing/UnifiedPlatformSection";
import { AIAssistantSection } from "@/components/landing/AIAssistantSection";
import { PrintReadySection } from "@/components/landing/PrintReadySection";
import { ImpactStats } from "@/components/landing/ImpactStats";
import { PricingSection } from "@/components/landing/PricingSection";
import { ComplianceSection } from "@/components/landing/ComplianceSection";
import { TestimonialsSection } from "@/components/landing/TestimonialsSection";
import { LatestArticlesSection } from "@/components/landing/LatestArticlesSection";
import { SEOHead } from "@/components/SEOHead";

import { useAppState } from "@/contexts/AppStateContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Index = () => {
  const { t, stripeUrl } = useLanguage();
  const { user, isInitializing, isAuthenticated, hasActiveSubscription } = useAppState();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isInitializing && isAuthenticated && hasActiveSubscription) {
      if (location.hash === '#pricing') {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isInitializing, isAuthenticated, hasActiveSubscription, location.hash, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-background">
      <SEOHead page="home" />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": t.faq1Question, "acceptedAnswer": { "@type": "Answer", "text": t.faq1Answer } },
              { "@type": "Question", "name": t.faq2Question, "acceptedAnswer": { "@type": "Answer", "text": t.faq2Answer } },
              { "@type": "Question", "name": t.faq3Question, "acceptedAnswer": { "@type": "Answer", "text": t.faq3Answer } },
              { "@type": "Question", "name": t.faq4Question, "acceptedAnswer": { "@type": "Answer", "text": t.faq4Answer } },
              { "@type": "Question", "name": t.faq5Question, "acceptedAnswer": { "@type": "Answer", "text": t.faq5Answer } },
              { "@type": "Question", "name": t.faq6Question, "acceptedAnswer": { "@type": "Answer", "text": t.faq6Answer } },
            ]
          })}
        </script>
      </Helmet>
      <SocialProofToast />
      
      {/* Sticky Header with Glassmorphism */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-nav">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={logoOuraTrust} alt="OuraTrust" className="h-6 object-contain" width={158} height={24} />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden sm:flex items-center gap-2 md:gap-4">
            <LanguageSwitcher />
            <Link to="/blog">
              <Button variant="ghost" className="text-muted-foreground">
                <BookOpen size={16} />
                {t.blog}
              </Button>
            </Link>
            <Link to="/demo">
              <Button variant="ghost" className="text-muted-foreground">
                {t.demo}
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="text-muted-foreground">
                {t.login}
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="shadow-button">
                {t.signup}
              </Button>
            </Link>
          </div>

          {/* Mobile Navigation */}
          <div className="flex sm:hidden items-center gap-2">
            <LanguageSwitcher />
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="sm:hidden bg-background/95 backdrop-blur-md border-t border-border"
            >
              <div className="container mx-auto px-4 py-4 flex flex-col gap-2">
                <Link to="/blog" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                    <BookOpen size={16} className="mr-2" />
                    {t.blog}
                  </Button>
                </Link>
                <Link to="/demo" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                    {t.demo}
                  </Button>
                </Link>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                    {t.login}
                  </Button>
                </Link>
                <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full shadow-button">
                    {t.signup}
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Hero with Mockup - More whitespace */}
      <section className="container mx-auto px-4 pt-36 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6 text-center lg:text-left"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success-light text-success text-sm font-medium"
            >
              <CheckCircle size={16} />
              {t.heroBadge}
            </motion.div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground leading-tight tracking-tight">
              {t.heroTitle1}{" "}
              <span className="bg-gradient-to-r from-primary to-emerald-500 bg-clip-text text-transparent">{t.heroTitle2}</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl">
              {t.heroSubtitle}
            </p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4"
            >
              <Link to="/signup?plan=pro">
                <Button size="lg" className="h-14 px-8 btn-premium text-base w-full sm:w-auto">
                  {t.ctaFree}
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/demo">
                <Button size="lg" variant="outline" className="h-14 px-8 text-base w-full sm:w-auto">
                  {t.ctaDemo}
                </Button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Right: Dashboard Mockup */}
          <div className="hidden lg:flex justify-center lg:justify-end">
            <HeroIllustration />
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <TrustBar />

      {/* Centralization Hub - Visual explanation */}
      <CentralizationHub />

      {/* Unified Platform Section - Multi-platform features */}
      <UnifiedPlatformSection />

      {/* 3 Steps Section */}
      <StepsSection />

      {/* AI Assistant Section - NEW */}
      <AIAssistantSection />

      {/* Impact Stats Banner - NEW */}
      <ImpactStats />

      {/* Compliance & Trust Section - NEW */}
      <ComplianceSection />

      {/* Features - More whitespace */}
      <section className="container mx-auto px-4 py-28">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          <div className="text-center p-8 rounded-2xl bg-card border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <QrCode className="text-primary" size={32} />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-3">{t.feature1Title}</h3>
            <p className="text-muted-foreground">{t.feature1Desc}</p>
          </div>

          <div className="text-center p-8 rounded-2xl bg-card border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-16 h-16 rounded-2xl bg-success/10 flex items-center justify-center mx-auto mb-6">
              <Shield className="text-success" size={32} />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-3">{t.feature2Title}</h3>
            <p className="text-muted-foreground">{t.feature2Desc}</p>
          </div>

          <div className="text-center p-8 rounded-2xl bg-card border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-16 h-16 rounded-2xl bg-warning/10 flex items-center justify-center mx-auto mb-6">
              <BarChart3 className="text-warning" size={32} />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-3">{t.feature3Title}</h3>
            <p className="text-muted-foreground">{t.feature3Desc}</p>
          </div>
        </motion.div>
      </section>

      {/* Print Ready Section */}
      <PrintReadySection />



      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Pricing Section - Multi-tier */}
      <PricingSection />

      {/* Latest Articles Section */}
      <LatestArticlesSection />

      {/* FAQ Section - More whitespace */}
      <section className="container mx-auto px-4 py-28 bg-slate-50/50">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto"
        >
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t.faqTitle}
            </h2>
            <p className="text-muted-foreground">{t.faqSubtitle}</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-card rounded-xl border border-slate-100 px-6">
              <AccordionTrigger className="text-left font-medium hover:no-underline">
                {t.faq1Question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {t.faq1Answer}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-card rounded-xl border border-slate-100 px-6">
              <AccordionTrigger className="text-left font-medium hover:no-underline">
                {t.faq2Question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {t.faq2Answer}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-card rounded-xl border border-slate-100 px-6">
              <AccordionTrigger className="text-left font-medium hover:no-underline">
                {t.faq3Question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {t.faq3Answer}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-card rounded-xl border border-slate-100 px-6">
              <AccordionTrigger className="text-left font-medium hover:no-underline">
                {t.faq4Question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {t.faq4Answer}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-card rounded-xl border border-slate-100 px-6">
              <AccordionTrigger className="text-left font-medium hover:no-underline">
                {t.faq5Question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {t.faq5Answer}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="bg-card rounded-xl border border-slate-100 px-6">
              <AccordionTrigger className="text-left font-medium hover:no-underline">
                {t.faq6Question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {t.faq6Answer}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
